/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import h from "../util/dom-builder";
import _ from "underscore";
import birdata from "../api.js";
import util from "../util/util.js";
import "../util/custom-bindings.js";

// TODO: move to utils
function performWithStatus(status, promiseFn) {
	status('busy');
	return promiseFn()
		.then(() => status('done'))
		.catch(e => {
			status('error');
			throw e;
		});
}

function count(items, predicate) {
	return items.reduce((n, item) => n + (predicate(item) ? 1 : 0), 0);
}

function Quiz(params) {
	params = params || util.hashParams();

	// When the quiz is displayed in a WebView embedded in the app, it needs to call a special callback to notify the app that the quiz is completed
	if(params.in_app) {
		params.onCompletion = () => {
			if(window.webkit) {
				window.webkit.messageHandlers.quizCompleted.postMessage(true);
			}
			if(window.android) {
				window.android.quizResult(true);
			}
		}
	}

	var quiz = quizzes[params.quiz];
	var startTime = +new Date();

	this.questions = quiz.questions.map(q => new Question(q));
	this.pageIndex = ko.observable(-1);
	this.currentQuestion = ko.pureComputed(() => this.questions[this.pageIndex()]);
	this.intro = quiz.intro;
	this.passingScore = quiz.passing_score;

	this.showSummary = ko.pureComputed(() => this.pageIndex() == this.questions.length);
	this.showIntro = ko.pureComputed(() => this.pageIndex() == -1);

	this.showNext = ko.pureComputed(() => this.pageIndex() < this.questions.length);

	this.title = ko.pureComputed(() => {
		if(this.showIntro()) {
			return quiz.title;
		} else if(this.showSummary()) {
			return 'Summary';
		} else {
			return 'Question ' + (this.pageIndex() + 1) + '/' + this.questions.length;
		}
	});

	this.allAnswers = () => this.questions.map(q => q.answers.indexOf(q.selectedAnswer()));

	this.next = () => {
		window.scrollTo(0, 0);
		this.pageIndex(this.pageIndex() + 1);
		if(this.pageIndex() == 0) {
			startTime = +new Date();
		}
		if(this.showSummary()) {
			this.submitResult();
		}
	}

	this.quizResult= () => {
		var now = +new Date();
		return {
			quiz: params.quiz,
			duration_in_seconds: Math.floor((now - startTime)/1000),
			score: this.score(),
			passed: this.passed()
		}
	}

	this.submitResult = () => {
		performWithStatus(this.submitStatus, () => {
			return birdata.submitQuizResult(this.quizResult())
		}).catch(e => {
			console.log(e);
		});
	};

	this.close = () => {
		if(this.pageIndex() == 0 || this.showSummary() || window.confirm("Your quiz progress will not be saved. Close quiz?")) {
			this.finish();
		}
	};

	this.submitStatus = ko.observable('none');
	this.showContinue = ko.pureComputed(() => this.submitStatus() == 'done' && this.passed() && params.onCompletion);
	this.showClose = ko.pureComputed(() => !params.in_app);
	this.showCloseButton = ko.pureComputed(() => this.showSummary() && !this.passed() && !params.in_app);
	this.check = () => this.currentQuestion().checked(true);
	this.score = ko.pureComputed(() => count(this.questions, q => q.answeredCorrectly()));
	this.passed = ko.pureComputed(() => this.score() >= quiz.passing_score);
	this.showCheck = ko.pureComputed(() => this.currentQuestion() && this.currentQuestion().selectedAnswer() != null && !this.currentQuestion().checked());
	this.showNext = ko.pureComputed(() => this.currentQuestion() && this.currentQuestion().checked());
	this.showReset = ko.pureComputed(() => this.showSummary() && !this.passed());
	this.reset = () => {
		this.questions.forEach(q => q.reset());
		this.pageIndex(0);
	};
	this.finish = params.onCompletion;

	window.onbeforeunload = () => {
		if(this.currentQuestion() && this.pageIndex() > 0) {
			return "Quiz not yet completed";
		}
	}
}

function Question(params) {
	this.title = params.title;
	this.image = params.image;

	var img = params.image ? h('img', {src: birdata.APP_ROOT_URL + params.image }) : undefined;

	this.html = params.html ||
		h('div', [
			h('h3', params.title),
			h('p', params.text),
			img ]).outerHTML;
	this.explanation = params.explanation;
	this.selectedAnswer = ko.observable(null);
	this.checked = ko.observable(false);
	this.answers = params.answers.map(a => new Answer(a, this));
	this.answeredCorrectly = () => this.selectedAnswer() ? this.selectedAnswer().correct : false;
	this.correctAnswer = this.answers.filter(a => a.correct)[0];
	this.reset = () => {
		this.checked(false);
		this.selectedAnswer(null);
	}
}

function Answer(params, question) {
	this.text = params.text;
	this.correct = !!params.correct;
	this.selected = ko.pureComputed(() => question.selectedAnswer() == this);
	this.css = ko.pureComputed(() => {
		if(question.checked()) {
			if(this.correct) {
				return 'correct';
			} else if(this.selected()) {
				return 'incorrect';
			}
		}
	});
}

ko.components.register('quiz', {
	viewModel: Quiz,
	template: require('../../templates/quiz.html')
});

var test_quiz = {
	title: 'Test Quiz',
	intro: "<p>Test quiz</p>",
	questions: [{
		title: "Basic math",
		text: '1 + 1 = ?',
		answers: [{
			text: '2',
			correct: true
		}, {
			text: '3'
		}, {
			text: 'window'
		}]
	}],
	passing_score: 1
};

var powl_quiz = {
	title: 'Powerful Owl Training Quiz',
	intro: "<p>All observers are required to complete the Powerful Owl Training Quiz.</p>",
	questions: [{
		title: 'Owl Surveying Ethics',
		text: 'As a Powerful Owl Project volunteer your role is to help protect a unique and amazing Threatened species. Which of the following things should you NOT do?',
		answers: [{
			text: 'Talk about the importance of Powerful Owls and their habitat'
		}, {
			text: 'Take other people to look at the owls in your allocated site',
			correct: true
		}, {
			text: 'Enjoy the opportunity to survey these fabulous birds'
		}, {
			text: 'Report your observations of owls to the project'
		}],
		explanation: '<p>When you sign up to volunteer as a citizen scientist working within the Powerful Owl Project you agree to keep secret the location of owls allocated to you to monitor. The locations of owl territories need to be protected, because roosting or breeding owls are easily disturbed and can be a static target for vandals and animal abusers who aim to cause harm, but also people may unintentionally disrupt the birds. Disturbance often results in severe negative consequences for Powerful Owls, including the abandonment of breeding and roost sites, and potentially even death. Keeping owl territories undisclosed to the public helps us to protect them against abuse – whether intentional or accidental.</p>'
	}, {
		title: 'Owl surveying ethics',
		text: 'You can take flash photography of Powerful Owls',
		answers: [{
			text: 'If you are a professional photographer with the appropriate equipment'
		}, {
			text: 'If you see cute chicks in the nest'
		}, {
			text: 'If the owls have their wings outstretched'
		}, {
			text: 'None of the above',
			correct: true
		}],
		explanation: '<p>Flash photography is extremely disruptive to Powerful Owls and it is mandated by the scientific license of the Powerful Owl Project that flash photography is not undertaken. Whilst there is debate about the impact of flash photography on nocturnal birds generally, flash photography is implicated in reducing foraging time, causing nest abandonment, and increasing susceptibility to epilepsy in owls.</p>'
	}, {
		title: 'Owl surveying ethics',
		text: 'How should you NOT use a torch when surveying for Powerful Owls?',
		answers: [{
			text: 'To shine continuously on the birds so you can observe their behaviour',
			correct: true
		}, {
			text: 'Pointing at the ground to help you find your way out of a site safely'
		}, {
			text: 'In short 10 second bursts'
		}, {
			text: 'Covered in a red or blue filter'
		}],
		explanation: '<p>Bright, white light torches can be highly disturbing to nocturnal birds.  Ideally you should use a torch with a red or blue filter. If you are using a white light torch, you should minimize light bursts to 10 seconds, to allow owls to continue their normal behaviour.</p>'
	}, {
		title: 'Owl surveying ethics',
		text: 'What sort of behaviours might you observe that would let you know a Powerful Owl is agitated?',
		answers: [{
			text: 'Foot withdrawing and kicking'
		}, {
			text: 'Head bobbing or staring'
		}, {
			text: 'A single focused hoot'
		}, {
			text: 'All of the above',
			correct: true
		}],
		explanation: '<p>Powerful Owls indicate they are agitated by head bobbing to better focus on the object they see as a threat. Intent staring, a kicking foot, head turning and weight shifting, or a single focused warning hoot often coupled with staring, are all indications your owl is agitated. If you see one or more of these behaviours, you need to move away from the owl/s immediately. They are clearly telling you “you are too close”.</p>'
	}, {
		title: 'WH & S',
		text: 'Before undergoing Powerful Owl monitoring, you must first:',
		answers: [{
			text: 'Complete all required registration forms, WH&S documents, acquire permissions and permits',
			correct: true
		}, {
			text: 'Be able to identify pellet contents without the help of a guide'
		}, {
			text: 'Have a surveying partner'
		}, {
			text: 'Be able to accurately identify a Powerful Owl call'
		}],
		explanation: '<p>It is important that you are properly registered and trained before undergoing monitoring, so you are covered by insurance in the case of an accident. You must be aware of the hazards involved with monitoring, and the ways in which you can avoid impacting on your birds, to make the process of surveying a positive one for both yourself and the owls.</p>'
	}, {
		title: 'WH & S',
		text: 'What should you do before conducting Powerful Owl surveys?',
		answers: [{
			text: 'Visit the site during the day time to assess hazards and risks',
			correct: true
		}, {
			text: 'Only visit the site at one time of day (i.e. only in the morning or only at night)'
		}, {
			text: 'Attempt to find the site alone, without a partner'
		}, {
			text: 'Find the brightest white-light torch you own and bring it along to survey'
		}],
		explanation: '<p>It is important to ground truth a field site before going there to survey it. You want to identify those key features of habitat that may be used by owls, and make sure you have identified any hazards on site. Sometimes recording behaviours at dusk means you will finish surveying after dark. Knowing where hazards are, and where the birds are if they are on site will ensure you leave a site safely, and that you do not disturb the birds.</p><p>When surveying, always make sure someone knows where you are, and preferably conduct surveying with a partner for safety reasons.</p>'
	}, {
		title: 'WH & S',
		text: 'What other precautions do you need to take when conducting owl surveys, especially at dusk or at night?',
		answers: [{
			text: 'Ensure you have told someone where you are going and when you expect to return so they can notify authorities if you do not return by the agreed time'
		}, {
			text: 'All of these things are important precautions',
			correct: true
		}, {
			text: 'Work in pairs as much as possible, and avoid parking vehicles or walking along roads'
		}, {
			text: 'At night, be aware of other people in parks or reserves and avoid places such as toilet blocks or public shelters which can be used for illegal activities'
		}],
		explanation: '<p>Carrying a phone is important when working in the field to help find your location and in case of an emergency, but it is also important to make sure someone knows where you are and when you expect to return so that an alarm can be raised more quickly if your phone is not working. Working in pairs is always safer than working alone.</p><p>When survey by car from the road it can be tempting when no one is around to pull off onto the shoulder and walk along the road.  This presents a risk from local traffic who may be travelling at speed, who are not used to anything being near the road at that location.  Please make sure your car is parked completely off the road and that you avoid walking along the road at night. Aside from traffic hazards, one of the biggest risks you will likely face will come from other people.  It is important to always be aware of your surroundings, and to avoid places which may attract people engaged in illegal activities. In areas that you feel are unsafe or may become unsafe (due to topography/people/anything else), DO NOT survey. Your safety is paramount.</p>'
	}, {
		title: 'WH & S',
		text: 'What precautions should you take when you encounter a Powerful Owl or other nocturnal bird?',
		answers: [{
			text: 'Leave a minimum of 30 metres space between yourself and the bird'
		}, {
			text: 'Refrain from using flash photography'
		}, {
			text: 'Limit shining your torch to 10 seconds or less at a time'
		}, {
			text: 'All of the above',
			correct: true
		}],
		explanation: '<p>It is important to maintain a buffer of 30 metres or more between yourself and an owl so they do not feel threatened. Flash photography and torches can also agitate owls and may cause them to abandon breeding or roost sites or adopt defensive behaviours.</p>'
	}, {
		title: 'WH & S',
		text: 'If you identify a male Powerful Owl guarding a nest tree should you:',
		answers: [{
			text: 'Allow him to see you as you enter and leave the site'
		}, {
			text: 'Walk close to the nest tree as you survey'
		}, {
			text: 'Linger around the core part of the territory'
		}, {
			text: 'None of the above',
			correct: true
		}],
		explanation: '<p>Powerful Owls invest much time and energy in breeding, and because of this are protective of their nest trees and young. Male Powerful Owls are particularly motivated to defend nest trees when the female is incubating, and both male and female owls will defend young chicks when they first fledge. This defense can result in swooping behaviour, as the owls try to drive away what they perceive as a threat. The best way to avoid swooping is to stay <b>at least 30m</b> from the nest tree and to ensure the male does not see you when you enter the site to survey. You should be aware of where the nest tree is, so you can monitor it from afar during the breeding season. Planning your route into and out of your site so you don’t walk past the nest tree is essential.</p>'
	}, {
		title: 'WH & S',
		text: 'How much time should a monitoring session take?',
		answers: [{
			text: 'as little time as is possible to gather the required data',
			correct: true
		}, {
			text: '20 minutes'
		}, {
			text: '1 hour'
		}, {
			text: 'enough time to bring a picnic'
		}],
		explanation: '<p>Minimizing time spent with the owls is the best way to gather data. Spending extra time with your owls can lead to habituation, a change in behaviour causing the owls to stop responding naturally to people as a potential threat. This change in behaviour can allow people to approach owls closely, always a problem if those approaching have ill intentions.</p>'
	}, {
		title: 'WH & S',
		text: 'What should you do if you get injured when out in the field?',
		answers: [{
			text: 'Soldier on'
		}, {
			text: 'Inform the project manager',
			correct: true
		}, {
			text: 'Rush to hospital'
		}, {
			text: 'Have a drink of rum'
		}],
		explanation: '<p>If you receive an injury that requires medical attention, that you find disturbing, that affects you or that is worrying, please record the time, date and circumstances of this injury and report it promptly to the Powerful Owl Project manager. This will allow an incident report to be generated, for any immediate or future claim to the Project’s insurers.</p>'
	}, {
		title: 'WH & S',
		text: 'What should you do if you observe an injured owl?',
		answers: [{
			text: 'Pick it up'
		}, {
			text: 'Quickly inform your local wildlife rescue organization',
			correct: true
		}, {
			text: 'Cover it with a towel or cloth'
		}, {
			text: 'Take it to the vet'
		}],
		explanation: '<p>Owls, like many nocturnal birds, have special feathers that allow silent flight.  Their feathers need to be in excellent condition to allow this flight. Covering an owl with a cloth, as we might do for an injured day bird may create a feather injury. Owls also have extremely strong talons for subduing prey. They need to be handled by trained professionals to avoid injury to the bird and handler, and to ensure their plumage remains as immaculate as possible. Your local wildlife rescue group is always your first point of contact if you find an injured owl.</p>'
	}, {
		title: 'WH & S',
		text: 'What should you do if you observe inappropriate behaviour around owl roost/nest sites?',
		answers: [{
			text: 'Confront the culprits'
		}, {
			text: 'Create a posse and go to war'
		}, {
			text: 'Inform local land manager',
			correct: true
		}, {
			text: 'Inform National Parks and Wildlife'
		}],
		explanation: '<p>If people are behaving inappropriately around Powerful Owls the best point of contact to quickly and effectively get help for your birds is your local land manager. A land manager may be a Bushcare Officer, Natural Areas Manager at your council, your local wildlife rescue group, or the police. Reporting to the Powerful Owl Project manager is important, as is reporting to National Parks, but it is often the land managers whom will be able to act most quickly to help the birds. Engaging with rowdy people should always be avoided.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'What color are the feathers of adult Powerful Owls?',
		answers: [{
			text: 'Solid, dark brown'
		}, {
			text: 'Brown and white, with dark v-shaped patterns on the chest',
			correct: true
		}, {
			text: 'Grey and white flecks and streaks'
		}, {
			text: 'Black and white with bars or spots'
		}],
		explanation: '<p>Powerful owls are often confused with other bird species, but their characteristic coloring helps in their accurate identification. Tawny frogmouths are grey and often confused for Powerful Owls. Powerful Owl stand over 50cm tall with dark brown coloring, with dark v-shaped chevrons on white or off-white chests. In combination with their colouring they have a grey eye mask with large yellow eyes and robust yellow feet with long, strong claws.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'What month does Powerful Owl Breeding begin?',
		answers: [{
			text: 'December'
		}, {
			text: 'July'
		}, {
			text: 'March',
			correct: true
		}, {
			text: 'October'
		}],
		explanation: '<p>In March, Powerful Owls begin breeding in shared roost trees. Eggs are laid in late May to early June. Incubation occurs from July to August. Fledglings disperse between December and March, but can sometimes stay in their home territory as late as May.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'What does the Powerful Owl call sound like?',
		answers: [{
			text: '“boo-book”'
		}, {
			text: 'Double “whoo hoo”',
			correct: true
		}, {
			text: 'Harsh, rasping screech in long or descending tones'
		}, {
			text: 'Soft, deep, repetitive, “ooo-ooo-ooo”'
		}],
		explanation: '<p>Powerful Owls have distinctive calls which can aid in their identification. They are often confused for Tawny Frogmouths, which have a deep, repetitive “ooo-ooo-ooo” call. Southern Boobooks, smaller owls who share forest spaces with Powerful Owls have a loud call that descends on the second note,  “boo-book”. The Powerful Owl’s call is a deep, double hoot “whoo-hoo”, that can descend or ascend on the second note.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'If you were looking for a Powerful Owl nest hollow what would you look for?',
		answers: [{
			text: 'low-hanging branches'
		}, {
			text: 'large tree hollows',
			correct: true
		}, {
			text: 'small tree hollows'
		}, {
			text: 'tree forks'
		}],
		explanation: '<p>Powerful Owls nest in large tree hollows. These tree hollows form in trees greater than 80cm diameter at the base and are usually 6-17m high. The entrance hole to the hollow is usually more than 35cm wide.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'What type of vegetation do Powerful Owls need to roost in?',
		answers: [{
			text: 'Vegetated wet areas near or on waterways'
		}, {
			text: 'Suburban areas with nearby forests'
		}, {
			text: 'Dry forests'
		}, {
			text: 'All of the above',
			correct: true
		}],
		explanation: '<p>Urban Powerful Owls often roost in dense vegetation along waterways but move into urban areas near forests to forage. When surveying for Powerful Owls, it is useful to follow waterways and gullies to look for whitewash or other signs of owl activity.  Whilst Powerful owls live in a variety of forest types, densely vegetated patches within these often form good roost areas.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'What sign would NOT indicate the presence of Powerful Owls activity?',
		answers: [{
			text: 'Pellets'
		}, {
			text: 'Whitewash'
		}, {
			text: 'Nests in tree branches',
			correct: true
		}, {
			text: 'Prey remains'
		}],
		explanation: '<p>Powerful Owls regurgitate indigestible pieces of their prey in pellets. Pellets look like hairy clumps of vacuum cleaner dust filled with bones or insect parts. Pellets under a tree are a good indicator of an owl roost. Powerful Owls produce a distinctive pattern of excretion. Their excrement, also called whitewash, is creamy white, without any darker areas, and looks like a circle of spilled paint or yoghurt. It is usually splashed in irregular splotches forming a circular pattern, rather than in concentrated in piles. Prey remains such as stomachs and intestines and clumps feathers deposited in a circle are good indicators of owl activity.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'When collecting data, which information should be reported to the Powerful Owl Project?',
		answers: [{
			text: 'All of the below',
			correct: true
		}, {
			text: 'Location of owl sighting position'
		}, {
			text: 'Number of adults and juveniles'
		}, {
			text: 'Prey in talons'
		}],
		explanation: '<p>Reporting data collected during your surveys in a timely manner is very important, as your reports may allow the Project to act quickly, if land managers need to modify their activities to better protect the owls. Collecting accurate locations for both breeding and roosting is an essential tool for advising land managers.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'How often should you survey your assigned site?',
		answers: [{
			text: 'Once a month'
		}, {
			text: 'Once a season'
		}, {
			text: 'Only during breeding months'
		}, {
			text: '2-4 times a month',
			correct: true
		}],
		explanation: '<p>Ideally, volunteers should visit their sites up to 4 times a month during the breeding season, and less frequently outside of the breeding season. This frequency strikes a balance between collecting good data and minimizing disturbance to the birds.  You should survey with greater frequency only around the time you might expect chicks to fledge. You will know this is imminent if you are hearing trilling from nest, or seeing chicks popping their heads up outside the hollow or standing at the edge of the hollow.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'When looking for Powerful Owl nests, you should:',
		answers: [{
			text: 'Identify large tree with hollows as potential nest sites during daylight'
		}, {
			text: 'Observe hollows at dusk, quietly and discreetly'
		}, {
			text: 'Listen quietly for chick trilling between August and September'
		}, {
			text: 'All of the above',
			correct: true
		}],
		explanation: '<p>It is important to quietly and discreetly survey Powerful Owls and their nesting sites, to avoid disturbing the birds, or eliciting defensive behaviour from guarding parent birds.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'How should you collect and store Powerful Owl pellets?',
		answers: [{
			text: 'Pick up the pellets and shove them in your pockets'
		}, {
			text: 'Pick up the pellets carefully (using gloves if you prefer), and place into a labelled paper bag',
			correct: true
		}, {
			text: 'Put all the pellets you find into one bundle and place into a plastic bag'
		}, {
			text: 'Break apart the pellet and only place bones into a bag'
		}],
		explanation: '<p>Pellets can be collected in any bag but ideally should be stored in paper bags, not plastic, to allow them to dry properly. All bags should be labelled with the date, collection location, and your name. Always wash your hands after dealing with pellets.</p>'
	}, {
		title: 'Powerful Owl Ecology',
		text: 'If you would like to publish a piece of writing using data collected during the project should you:',
		answers: [{
			text: 'create an interesting and valuable document that helps us all better understand owls'
		}, {
			text: 'consult with the Project Manager prior to publishing'
		}, {
			text: 'appropriately acknowledge all participants of the project'
		}, {
			text: 'All of the above',
			correct: true
		}],
		explanation: '<p>The Powerful Owl Project encourages your ideas, insights and input into the research. We love to collaborate to create great science that lets us better understand owls. Data collected during the project must be appropriately acknowledged, as the work of many hands generates the knowledge we all share. To collaborate effectively please consult with the Project Manager prior to publishing your document, so together we can create something great.</p>'
	}],
	passing_score: 21
}

var glossy_quiz = {
    "title": "Glossy Count Quiz",
    "intro": "<p>This quiz is designed to test your knowledge on:</p> <ul> <li>identifying Glossy Black-Cockatoos and their feed trees, and</li> <li>how to safely collect data in the field.</li> </ul> <p>Pass this quiz to access to the site-booking system which you can use to reserve a survey site for the next Glossy count.</p> <p>This quiz has 20 multiple choice questions and is untimed. It will take newly-trained citizen scientists around 10 minutes to complete, and even less time for those familiar with Glossies.</p> <p> If you would like to train for this quiz, training events and materials can be found <a target=\"_blank\" href=\"https://bit.ly/glossyproject\">here</a>. </p>",
    "passing_score": 16,
    "questions": [
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q1.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female",
                    "correct": true
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p> This bird should be recorded as an <b>adult female.</b> </p> <p> Yellow facial feathers and orange-yellow tail panels suggest it is a <b>female</b>. <br> The lack of pale yellow scalloping on the chest/belly suggests it is not a fledgling. </p> <p class=\"credit\"> Photo: Geoff Walker <a href=\"https://www.inaturalist.org/observations/108708842\" target=\"_blank\">https://www.inaturalist.org/observations/108708842</a> </p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q2.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male",
                    "correct": true
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p> This bird should be recorded as a <b>juvenile/subadult male</b>. </p> <p> The lack of yellow facial feathers, combined with mostly red tail panels, suggest it is a <b>male</b>. <br> Black bars on the tail suggest it is a <b>juvenile/subadult</b>. </p> <p> The lack of pale yellow scalloping on the chest/belly, and lack of small, pale yellow spots on the cheeks suggest it is not a fledgling. </p> <p class=\"credit\">Photo: Jayden Gunn</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q3.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy",
                    "correct": true
                }
            ],
            "explanation": "<p> This is not a Glossy Black-Cockatoo, it is a <b>Yellow-tailed Black-Cockatoo</b>, as indicated by the solid yellow cheek patch. </p> <p> This bird can be recorded in Birdata’s “General” program. </p> <p class=\"credit\"> Photo: “birdsaspoetry” <a target=\"_blank\" href=\"https://flic.kr/p/2m7o4Nx\">https://flic.kr/p/2m7o4Nx</a> </p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q4.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy",
                    "correct": true
                }
            ],
            "explanation": "<p> This is not a Glossy Black-Cockatoo, it is a male <b>Red-tailed Black-Cockatoo</b>. </p> <p> Red-tailed Black-Cockatoos are larger than Glossy Black-Cockatoos, and have a larger crest and darker black plumage. You are more likely to see Red-tailed Black‑Cockatoos in Queensland and northern New South Wales. </p> <p> This bird can be recorded in Birdata’s “General” program. </p> <p class=\"credit\"> Photo: Jean and Fred Hort <a href=\"https://flic.kr/p/2kyJjDu\" target=\"_blank\">https://flic.kr/p/2kyJjDu</a> </p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q5.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling",
                    "correct": true
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p> This bird should be recorded as a <b>fledgling</b>. </p> <p> Pale yellow scalloping on the chest/belly help identify it as a <b>fledgling</b>. Fledglings stay close to their parents and are dependent on their parents to feed them, so they can often be heard begging for food. </p> <p> It is difficult to tell the sex of fledglings; in Birdata you can record their sex as “<b>Unsure</b>”. </p> <p class=\"credit\"> Photo: Peter Murrell </p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q6.jpg",
            "text": "What kind of Glossy is this?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy",
                    "correct": true
                }
            ],
            "explanation": "<p>This is not a Glossy Black-Cockatoo, it is a female <b>Red-tailed Black-Cockatoo</b>.</p> <p>Red-tailed Black-Cockatoos are larger than Glossy Black-Cockatoos and adult females have yellow scalloping on their chest/belly, head, and wings. You are more likely to see Red-tailed Black‑Cockatoos in Queensland and northern New South Wales.</p> <p>This bird can be recorded in Birdata’s “General” program.</p> <p class=\"credit\">Photo: Jean and Fred Hort <a href=\"https://flic.kr/p/2kDRg75\" target=\"_blank\">https://flic.kr/p/2kDRg75</a></p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q7.jpg",
            "text": "What kind of Glossy is the bird on the LEFT?",
            "answers": [
                {
                    "text": "Adult male",
                    "correct": true
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p>This bird should be recorded as an <b>adult male</b>.</p> <p> The lack of yellow facial feathers suggest it is a <b>male</b>. <br> Solid red tail panels (i.e., without black bars) suggest it is an <b>adult</b>. </p> <p> The lack of pale yellow scalloping on the chest/belly, and lack of small, pale yellow spots on the cheeks suggest it is not a fledgling. </p> <p class=\"credit\">Photo: Peter Murrell</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q8.jpg",
            "text": "What kind of Glossy is the bird on the RIGHT?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female",
                    "correct": true
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p>This bird should be recorded as an <b>adult female</b>.</p> <p> Yellow facial feathers and orange-yellow tail panels suggest it is a <b>female</b>. <br> The lack of pale yellow scalloping on the chest/belly suggest it is not a fledgling. </p> <p>Often, when you encounter a single pair of Glossies, they will be a male and a female. If you are unsure, take a photo and include this with your Birdata record.</p> <p class=\"credit\">Photo: Peter Murrell</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q9.jpg",
            "text": "What kind of Glossy is the bird on the LEFT?",
            "answers": [
                {
                    "text": "Adult male"
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male",
                    "correct": true
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p>This bird should be recorded as a <b>juvenile/subadult male</b>.</p> <p> The lack of yellow facial feathers suggests it is a <b>male</b>. <br> Black bars on the tail suggest it is a <b>juvenile/subadult</b>. </p> <p>The lack of pale yellow scalloping on the chest/belly and lack of small, pale yellow spots on the cheeks suggest it is not a fledgling.</p> <p class=\"credit\">Photo: Peter Murrell</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoos",
            "image": "images/quiz/gbc/q10.jpg",
            "text": "What kind of Glossy is the bird on the RIGHT?",
            "answers": [
                {
                    "text": "Adult male",
                    "correct": true
                },
                {
                    "text": "Adult female"
                },
                {
                    "text": "Juvenile/subadult male"
                },
                {
                    "text": "Fledgling"
                },
                {
                    "text": "Not a Glossy"
                }
            ],
            "explanation": "<p>This bird should be recorded as an <b>adult male</b>.</p> <p> The lack of yellow facial feathers suggests it is a <b>male</b>. <br> Solid red tail panels (i.e., without black bars) suggest it is an <b>adult</b>. </p> <p>The lack of pale yellow scalloping on the chest/belly and lack of small, pale yellow spots on the cheeks suggest it is not a fledgling.</p> <p class=\"credit\">Photo: Peter Murrell</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoo feed trees",
            "text": "Glossy Black-Cockatoos feed almost exclusively on which tree?",
            "answers": [
                {
                    "text": "Banksia"
                },
                {
                    "text": "Eucalyptus"
                },
                {
                    "text": "She-oak",
                    "correct": true
                },
                {
                    "text": "Pine"
                }
            ],
            "explanation": "<p>Glossy Black-Cockatoos feed almost exclusively on seeds in the cones of she-oak (<i>Casuarina/Allocasuarina</i>) trees.</p> <p>Glossies may perch or roost in other plants, however they are unlikely to feed on them. If you observe a black-cockatoo feeding in a plant other than a she-oak, it will probably be a Red-tailed Black-Cockatoo or Yellow-tailed Black-Cockatoo.</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoo feed trees",
            "image": "images/quiz/gbc/q12.jpg",
            "text": "What is a Glossy Black-Cockatoo “feed tree”?",
            "answers": [
                {
                    "text": "Any she-oak tree"
                },
                {
                    "text": "Any she-oak tree with cones on it"
                },
                {
                    "text": "A she-oak tree which Glossies are observed feeding on, or which has chewings beneath it",
                    "correct": true
                },
                {
                    "text": "Any tree or plant with a Glossy in it"
                }
            ],
            "explanation": "<p>“Feed trees” are she-oaks which Glossy Black-Cockatoos are observed feeding on OR have chewings beneath them.</p> <p>She-oaks with cones (but no chewings) are potential feed trees only, as we do not know whether Glossies feed on them; Glossies do not feed on all she-oaks with cones.</p> <p class=\"credit\">Photo: Jayden Gunn</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoo feed trees",
            "image": "images/quiz/gbc/q13.jpg",
            "text": "Which of these is a she-oak tree?",
            "answers": [
                {
                    "text": "A"
                },
                {
                    "text": "B"
                },
                {
                    "text": "C"
                },
                {
                    "text": "D",
                    "correct": true
                }
            ],
            "explanation": "<p>The correct answer is D: She-oaks can be identified by their long, needle-like foliage. They may also have hard cones around the size of an olive (5 – 50 mm in length).</p> <p class=\"credit\">Photo D: John Tann <a href=\"https://www.flickr.com/photos/31031835@N08/\" target=\"_blank\">https://www.flickr.com/photos/31031835@N08/</a></p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoo feed trees",
            "image": "images/quiz/gbc/q14.jpg",
            "text": "What sort of information can chewings provide?",
            "answers": [
                {
                    "text": "The presence of Glossies at a location"
                },
                {
                    "text": "Which trees are Glossy feed trees"
                },
                {
                    "text": "Approximate time since a Glossy last visited a feed tree"
                },
                {
                    "text": "Which feed trees are preferred over others"
                },
                {
                    "text": "All of the above",
                    "correct": true
                }
            ],
            "explanation": "<p>Chewings are evidence of Glossy Black-Cockatoo presence at a location, and the colour of chewings can indicate when Glossies last visited. The number of chewings underneath a feed tree can indicate which are the preferred feed trees.</p> <p class=\"credit\">Photo: Laura X Tan</p>"
        },
        {
            "title": "Identifying Glossy Black-Cockatoo feed trees",
            "image": "images/quiz/gbc/q15.jpg",
            "text": "If chewings under a tree are whitish-green in colour, what does this usually mean?",
            "answers": [
                {
                    "text": "Glossies fed on the tree days ago",
                    "correct": true
                },
                {
                    "text": "Glossies fed on the tree weeks ago"
                },
                {
                    "text": "Glossies fed on the tree months ago"
                },
                {
                    "text": "It doesn’t mean anything"
                }
            ],
            "explanation": "<p>The colour of chewings can indicate when birds last visited a tree:</p> <ul> <li>Whitish-green chewings suggest glossies fed on the tree days ago;</li> <li>orange-red chewings suggest they fed on the tree weeks ago; and</li> <li>brown-grey chewings suggest they fed on the tree months ago.</li> </ul> <p class=\"credit\">Photo: D Bryant (DELWP 2021)</p>"
        },
        {
            "title": "Safely collecting data in the field",
            "text": "If you find a Glossy Black-Cockatoo, what should you do?",
            "answers": [
                {
                    "text": "Make lots of noise as you celebrate"
                },
                {
                    "text": "Minimise disturbance to the bird",
                    "correct": true
                },
                {
                    "text": "Attract the bird’s attention to get a clear photo and identification"
                },
                {
                    "text": "Share the location of the bird with as many people as possible"
                },
                {
                    "text": "All of the above"
                }
            ],
            "explanation": "<p>Always minimise disturbance to native birds. Disturbing birds can harm them by disrupting their normal behaviour.</p> <p>Keep the location of Glossies to yourself and Birdata, to protect them from poachers and people who may disturb them.</p>"
        },
        {
            "title": "Safely collecting data in the field",
            "text": "Before you go out to collect data, what safety precautions should you take?",
            "answers": [
                {
                    "text": "Pack sunscreen, insect repellent and a first aid kit"
                },
                {
                    "text": "Charge your power bank and/or pack a satellite phone (for remote areas)"
                },
                {
                    "text": "Tell someone where you are going and when you expect to return so they can notify authorities if you do not return on time"
                },
                {
                    "text": "Pack food and plenty of water"
                },
                {
                    "text": "Organise the field trip with one or more friends"
                },
                {
                    "text": "All of the above",
                    "correct": true
                }
            ],
            "explanation": "<p>Your safety is the priority. Going out to the field in a pair or small group will ensure someone is there to help (or get help) if something goes wrong.</p>"
        },
        {
            "title": "Safely collecting data in the field",
            "image": "images/quiz/gbc/q18.jpg",
            "text": "Which of the following is NOT something you should do at your field site?",
            "answers": [
                {
                    "text": "Familiarise yourself with the site, including entry and exit points, on arrival"
                },
                {
                    "text": "Confront people conducting suspicious activity",
                    "correct": true
                },
                {
                    "text": "Assess whether the site is safe, or whether it has been impacted by floods or recent fire"
                },
                {
                    "text": "Wear a broad-brimmed hat, protective clothing (a long-sleeve top and long pants), and comfortable, closed-toe shoes"
                },
                {
                    "text": "Re-apply sunscreen and insect repellent every few hours"
                }
            ],
            "explanation": "<p>Your safety is the priority. If something at your site looks unsafe or makes you feel uncomfortable, do not commence or continue your survey. Avoid confronting people engaging in suspicious activity.</p> <p class=\"credit\">Photo: Laura X Tan</p>"
        },
        {
            "title": "Safely collecting data in the field",
            "image": "images/quiz/gbc/q19.jpg",
            "text": "In what circumstances should you cancel plans to travel to your field site?",
            "answers": [
                {
                    "text": "There is a severe weather warning close to the survey site (including high fire ratings, flood, or gale force winds)"
                },
                {
                    "text": "You feel too unwell to go out"
                },
                {
                    "text": "You cannot access the site"
                },
                {
                    "text": "A hazard reduction burn has been planned close to your survey site location, on or within a few weeks of the day you plan to go there"
                },
                {
                    "text": "Any of the above",
                    "correct": true
                }
            ],
            "explanation": "<p>Your safety is the priority. If anything makes you feel uncomfortable or suggests the site may be unsafe, do not proceed. Be aware of changing weather conditions, and how these may affect access to your site during your survey.</p> <p class=\"credit\">Image: Bureau of Meteorology website <a href=\"http://www.bom.gov.au/australia/warnings/index.shtml\" target=\"_blank\">http://www.bom.gov.au/australia/warnings/index.shtml</a> </p>"
        },
        {
            "title": "Safely collecting data in the field",
            "text": "What should you do if you get injured while out in the field?",
            "answers": [
                {
                    "text": "Soldier on"
                },
                {
                    "text": "Apply first aid and contact Triple Zero if it is an emergency",
                    "correct": true
                },
                {
                    "text": "Call BirdLife"
                },
                {
                    "text": "Don’t tell anyone"
                }
            ],
            "explanation": "<p>For all emergencies, immediately contact Triple Zero by calling 000.</p> <p>If you receive an injury that requires medical attention or affects your ability to proceed with collecting data, record the incident (date, time, cause, and effect), and report it to the Project Coordinator later on.</p>"
        }
    ]
};

var quizzes = {
	powl: powl_quiz,
	test: test_quiz,
	glossy: glossy_quiz
};
